"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  DayPicker,
  type DayPickerProps,
  type ChevronProps,
  type DropdownProps,
} from "react-day-picker";
import { UI, SelectionState, DayFlag } from "react-day-picker";
import { cn } from "@/shared/lib/cn";

export type CalendarProps = DayPickerProps;

// Chevron override: renders lucide ChevronLeft or ChevronRight based on orientation.
// The `orientation` prop is provided by DayPicker ("left" | "right" | "up" | "down").
function CalendarChevron({ orientation, ...rest }: ChevronProps) {
  const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
  return (
    <Icon
      size={16}
      strokeWidth={2}
      aria-hidden="true"
      className={cn(
        "text-grey-500",
        // hide up/down chevrons (not used in our layout)
        orientation !== "left" && orientation !== "right" && "hidden",
      )}
      {...(rest as React.SVGProps<SVGSVGElement>)}
    />
  );
}

// Dropdown override (used when captionLayout="dropdown"): a brand-themed native <select>
// for month / year navigation. Rendering our own <select> avoids fighting react-day-picker's
// default hidden-select DOM, which relies on the (unimported) default stylesheet.
function CalendarDropdown({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
}: DropdownProps) {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={onChange}
      className={cn(
        "cursor-pointer rounded-control border border-grey-200 bg-white px-1.5 py-0.5",
        "text-[13px] font-medium text-grey-800 outline-none transition-colors",
        "hover:bg-grey-100 focus-visible:ring-1 focus-visible:ring-green",
      )}
    >
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// Brand-themed calendar built on react-day-picker DayPicker (v10).
// Uses Tailwind design tokens: green #28A745 for selected day, grey-200 grid borders,
// rounded-control radius, Inter font inherited from the page body.
// Exported as `Calendar` so callers can use it standalone or inside DatePicker's Popover.
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      components={{ Chevron: CalendarChevron, Dropdown: CalendarDropdown }}
      className={cn("p-3 font-sans text-[13px] text-grey-800", className)}
      classNames={{
        // -- Layout --
        // Root is the positioning anchor for the absolutely-placed Nav (see UI.Nav below).
        // Without `relative` the Nav anchors to the Popover content and overlays the day
        // grid, swallowing day clicks — that was the "nothing selectable" bug.
        [UI.Root]: "relative w-full",
        [UI.Months]: "flex flex-col gap-4",
        [UI.Month]: "flex flex-col gap-2",
        [UI.MonthCaption]: "flex items-center justify-center h-7 px-8",
        [UI.CaptionLabel]: "text-[13px] font-medium text-grey-800",
        // Month + year dropdowns (captionLayout="dropdown")
        [UI.Dropdowns]: "flex items-center justify-center gap-1.5",
        // -- Navigation: bounded to the caption row at the top, never over the day grid.
        // pointer-events-none on the bar + pointer-events-auto on the buttons keeps the
        // empty middle from intercepting clicks even if the bar spans wide.
        [UI.Nav]:
          "absolute top-3 inset-x-3 flex items-center justify-between h-7 pointer-events-none",
        [UI.PreviousMonthButton]: cn(
          "pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-control border border-grey-200",
          "text-grey-500 hover:bg-grey-100 hover:text-grey-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
        ),
        [UI.NextMonthButton]: cn(
          "pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-control border border-grey-200",
          "text-grey-500 hover:bg-grey-100 hover:text-grey-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
        ),
        // -- Month grid --
        [UI.MonthGrid]: "w-full border-collapse",
        [UI.Weekdays]: "flex",
        [UI.Weekday]: "flex-1 text-center text-[11px] font-medium text-grey-400 py-1",
        [UI.Weeks]: "flex flex-col gap-0.5",
        [UI.Week]: "flex",
        // -- Day cells --
        [UI.Day]: "flex-1 flex items-center justify-center p-0",
        [UI.DayButton]: cn(
          "mx-auto flex h-8 w-8 items-center justify-center rounded-control text-[13px] font-normal",
          "transition-colors hover:bg-green-bg hover:text-green focus-visible:outline-none",
          "focus-visible:ring-1 focus-visible:ring-green focus-visible:ring-offset-1",
          "cursor-pointer",
        ),
        // -- Selection state modifiers (applied alongside DayButton) --
        [SelectionState.selected]:
          "bg-green text-white hover:bg-green-dark rounded-control",
        [SelectionState.range_start]: "bg-green text-white rounded-l-control",
        [SelectionState.range_end]: "bg-green text-white rounded-r-control",
        [SelectionState.range_middle]: "bg-green-bg text-green rounded-none",
        // -- Day flags --
        [DayFlag.today]: "border border-grey-300",
        [DayFlag.outside]: "text-grey-400 opacity-50",
        [DayFlag.disabled]: "text-grey-300 cursor-not-allowed hover:bg-transparent",
        [DayFlag.hidden]: "invisible",
        // -- Spread caller overrides on top --
        ...classNames,
      }}
      {...props}
    />
  );
}
